<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const platformLabel = computed(() =>
  props.context.platform === 'instagram'
    ? t('CONVERSATION.META_COMMENT_CONTEXT.PLATFORMS.INSTAGRAM')
    : t('CONVERSATION.META_COMMENT_CONTEXT.PLATFORMS.FACEBOOK')
);

const authorLabel = computed(() => {
  if (props.context.authorUsername) return `@${props.context.authorUsername}`;
  if (props.context.authorName) return props.context.authorName;
  return t('CONVERSATION.META_COMMENT_CONTEXT.UNKNOWN_AUTHOR');
});

const sourceTypeLabel = computed(() => {
  const labels = {
    post: t('CONVERSATION.META_COMMENT_CONTEXT.SOURCE_TYPES.POST'),
    reel: t('CONVERSATION.META_COMMENT_CONTEXT.SOURCE_TYPES.REEL'),
    story: t('CONVERSATION.META_COMMENT_CONTEXT.SOURCE_TYPES.STORY'),
    live: t('CONVERSATION.META_COMMENT_CONTEXT.SOURCE_TYPES.LIVE'),
  };

  return labels[props.context.sourceObjectType?.toLowerCase()] || labels.post;
});
</script>

<template>
  <div class="px-4 pt-2 pb-1" data-clarity-mask="True">
    <div
      class="flex items-start w-full gap-3 p-3 border rounded-xl bg-n-alpha-1 border-n-amber-5"
    >
      <div
        class="flex items-center justify-center rounded-lg size-10 bg-n-amber-3 text-n-amber-11 flex-shrink-0"
      >
        <span class="i-lucide-message-square-reply size-5" />
      </div>

      <div class="flex flex-col min-w-0 flex-1 gap-1">
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-xs font-medium text-n-amber-11">
            {{ t('CONVERSATION.META_COMMENT_CONTEXT.TITLE') }}
          </span>
          <span
            class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-slate-3 text-n-slate-11"
          >
            {{ platformLabel }}
          </span>
          <span class="text-[10px] text-n-slate-9">
            {{ sourceTypeLabel }}
          </span>
        </div>

        <span class="text-xs font-medium text-n-slate-11">
          {{ authorLabel }}
        </span>

        <p
          v-if="context.content"
          class="m-0 text-sm whitespace-pre-line text-n-slate-12"
        >
          {{ context.content }}
        </p>

        <a
          v-if="context.sourceUrl"
          :href="context.sourceUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="w-fit text-xs font-medium text-n-blue-11 hover:underline"
        >
          {{ t('CONVERSATION.META_COMMENT_CONTEXT.OPEN_SOURCE') }}
        </a>
      </div>
    </div>
  </div>
</template>
